import { useState, useEffect, useRef, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { ProgressSpinner } from "primereact/progressspinner";
import "./ListOfProducts.scss";
import { useTranslation } from "react-i18next";

export interface ListOfProductsProps {
  visible: boolean;
  handleClose: () => void;
  productsOverview: {
    productName: string;
    warehouseName: string;
    productOverview: number;
  }[];
  buttonInactiveStyle?: React.CSSProperties;
}

export const ListOfProducts = (p: ListOfProductsProps) => {
  const { visible, handleClose, productsOverview, buttonInactiveStyle } = p;

  const { t } = useTranslation();

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) addButtonInactiveStyle = buttonInactiveStyle;

  const [loading, setLoading] = useState(false);
  const lastShownSignatureRef = useRef<string>("");

  const currentSignature = useMemo(() => {
    if (!productsOverview || productsOverview.length === 0) return "";
    const name = productsOverview[0]?.productName ?? "";
    return `${name}__len=${productsOverview.length}`;
  }, [productsOverview]);

  useEffect(() => {
    if (visible) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (!currentSignature) {
      setLoading(true);
      return;
    }

    if (currentSignature !== lastShownSignatureRef.current) {
      lastShownSignatureRef.current = currentSignature;
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [visible, currentSignature]);

  const isLoading =
    visible && (loading || !productsOverview || productsOverview.length === 0);

  const productName = useMemo(() => {
    if (!productsOverview || productsOverview.length === 0) return "";
    return productsOverview[0]?.productName ?? "";
  }, [productsOverview]);

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-shop-window" style={{ fontSize: 16 }}></i>
      </span>
      <span>
        {" "}
        {`${t("Stanje proizvoda")} ${
          isLoading ? t("Učitavanje...") : productName
        } ${t("po magacinima")}`}
      </span>
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Zatvori")}
        onButtonClick={handleClose}
        addStyle={addButtonInactiveStyle}
      />
    </div>
  );

  return (
    <>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: "50vw" }}
        className="list-of-products-modal"
        footer={footer}
        blockScroll={false}
        draggable={false}
        onHide={handleClose}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "2rem 0",
            }}
          >
            <ProgressSpinner
              style={{ width: "60px", height: "60px" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #198754",
                }}
              >
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Magacin")}
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    borderBottom: "1px solid #198754",
                  }}
                >
                  {t("Stanje")}
                </th>
              </tr>
            </thead>
            <tbody>
              {productsOverview.map((selectedProduct) => (
                <tr style={{ borderBottom: "1px solid #198754" }}>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {selectedProduct.warehouseName?.toUpperCase()}
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      borderBottom: "1px solid #198754",
                    }}
                  >
                    {!Number.isInteger(selectedProduct.productOverview)
                      ? selectedProduct.productOverview.toFixed(3)
                      : selectedProduct.productOverview.toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Dialog>
    </>
  );
};
